'use client';

import { usePathname } from 'next/navigation';
import { useTranslation } from 'react-i18next';
import type { CSSProperties, ReactNode } from 'react';
import { PortalLink } from '@akademi/site-builder-runtime';

/**
 * Shared "sign in" affordance every island uses when the user is
 * anonymous. Three visual treatments cover every host-coupled
 * mounting site:
 *
 * <ul>
 *   <li>{@code button} — solid CTA, looks like the real action.</li>
 *   <li>{@code inline} — italic link, fits beside other meta.</li>
 *   <li>{@code card} — bordered card with longer copy.</li>
 * </ul>
 */
export interface AnonymousFallbackProps {
  variant?: 'button' | 'inline' | 'card';
  prompt?: ReactNode;
  /** Sign-in URL. Default {@code /sign-in?returnTo=<current>}. Hosts
   *  override when their auth path differs. */
  signInHref?: string;
}

export function AnonymousFallback({
  variant = 'inline',
  prompt,
  signInHref,
}: AnonymousFallbackProps) {
  const { t } = useTranslation('site-builder-blocks');
  const pathname = usePathname() ?? '';
  const href = signInHref ?? `/sign-in?returnTo=${encodeURIComponent(pathname)}`;
  const message = prompt ?? t('islands.signIn');

  if (variant === 'button') {
    return (
      <PortalLink href={href} style={btnStyle}>
        {message}
      </PortalLink>
    );
  }

  if (variant === 'card') {
    return (
      <div
        style={{
          padding: 'clamp(16px, 2.5vw, 24px)',
          borderRadius: 'var(--radius-md, 8px)',
          background: 'var(--color-surface-card, rgba(127,127,127,0.04))',
          border: '1px solid var(--color-border-subtle, rgba(0,0,0,0.08))',
          textAlign: 'center',
        }}
      >
        <div
          style={{
            fontSize: 'var(--font-size-sm, 14px)',
            color: 'var(--color-text-secondary, #5f6675)',
            marginBottom: 12,
          }}
        >
          {message}
        </div>
        <PortalLink
          href={href}
          style={{
            ...btnStyle,
            padding: 'var(--space-2-5, 10px) var(--space-5, 20px)',
            fontSize: '13px',
          }}
        >
          {t('islands.signIn')}
        </PortalLink>
      </div>
    );
  }

  return (
    <PortalLink
      href={href}
      style={{
        fontSize: '13px',
        color: 'var(--color-text-brand, var(--color-brand-600, #3b5bdb))',
        textDecoration: 'none',
        fontStyle: 'italic',
      }}
    >
      {message} →
    </PortalLink>
  );
}

const btnStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 'var(--space-3, 12px) var(--space-6, 24px)',
  borderRadius: 'var(--radius-md, 8px)',
  background: 'var(--color-brand-500, #3b5bdb)',
  color: 'var(--color-text-on-brand, #fff)',
  textDecoration: 'none',
  fontWeight: 600,
  fontSize: 'var(--font-size-sm, 14px)',
  minHeight: 44,
};

/** Per-island skeleton variants. */
export function ButtonSkeleton({ width = '180px' }: { width?: string } = {}) {
  return (
    <div
      style={{
        display: 'inline-block',
        width,
        height: 36,
        borderRadius: 'var(--radius-md, 8px)',
        background:
          'linear-gradient(90deg, rgba(127,127,127,0.08), rgba(127,127,127,0.16), rgba(127,127,127,0.08))',
        backgroundSize: '200% 100%',
        animation: 'sb-island-pulse 1.5s ease-in-out infinite',
      }}
    />
  );
}

export function InlineSkeleton({ width = '120px' }: { width?: string } = {}) {
  return (
    <span
      style={{
        display: 'inline-block',
        width,
        height: 12,
        borderRadius: 6,
        background:
          'linear-gradient(90deg, rgba(127,127,127,0.08), rgba(127,127,127,0.16), rgba(127,127,127,0.08))',
        backgroundSize: '200% 100%',
        animation: 'sb-island-pulse 1.5s ease-in-out infinite',
      }}
    />
  );
}

export function CardSkeleton({ height = 120 }: { height?: number } = {}) {
  return (
    <div
      style={{
        height,
        borderRadius: 'var(--radius-md, 8px)',
        background:
          'linear-gradient(90deg, rgba(127,127,127,0.08), rgba(127,127,127,0.16), rgba(127,127,127,0.08))',
        backgroundSize: '200% 100%',
        animation: 'sb-island-pulse 1.5s ease-in-out infinite',
      }}
    />
  );
}

/** Inject keyframes once. */
export function IslandKeyframes() {
  return (
    <style
      dangerouslySetInnerHTML={{
        __html: `@keyframes sb-island-pulse { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }`,
      }}
    />
  );
}

/** Shared toggle-button style helpers. */
export const toggleBase: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  padding: 'var(--space-2-5, 10px) var(--space-4, 16px)',
  borderRadius: 'var(--radius-md, 8px)',
  fontSize: '13px',
  fontWeight: 600,
  border: '1px solid transparent',
  cursor: 'pointer',
  minHeight: 36,
  transition: 'background 120ms ease-out, border-color 120ms ease-out',
};

export const toggleInactive: CSSProperties = {
  ...toggleBase,
  background: 'var(--color-surface-card, rgba(127,127,127,0.08))',
  color: 'var(--color-text-primary, #0f1218)',
  borderColor: 'var(--color-border-subtle, rgba(0,0,0,0.12))',
};

export const toggleActive: CSSProperties = {
  ...toggleBase,
  background: 'var(--color-brand-50, #e6efff)',
  color: 'var(--color-text-brand, var(--color-brand-700, #2b3eb0))',
  borderColor: 'var(--color-brand-200, #c2d3f7)',
};

export const toggleError: CSSProperties = {
  ...toggleBase,
  background: 'rgba(220, 38, 38, 0.08)',
  borderColor: 'rgba(220, 38, 38, 0.25)',
  color: 'var(--color-danger, #dc2626)',
};

/**
 * Inline error caption rendered under a toggle button when a mutation
 * fails. Uses {@code role="alert"} + {@code aria-live="polite"} so
 * screen readers announce the failure without interrupting the user.
 */
export function ToggleErrorCaption({ message }: { message: string }) {
  return (
    <div
      role="alert"
      aria-live="polite"
      style={{
        marginTop: 6,
        fontSize: 'var(--font-size-xs, 12px)',
        color: 'var(--color-danger, #dc2626)',
        lineHeight: 1.3,
      }}
    >
      {message}
    </div>
  );
}
